import { useProfile } from "@/context/useProfileProps";
import { getLastDateOfMonth, getMonthNumber } from "@/helpers/getMonthNumber";
import { getNoticePeriodToDays } from "@/helpers/getNoticePeriod";
import { calculateDaysUntil, extractValues } from "@/helpers/helper";
import { shouldShowSalaryNudge } from "@/helpers/shouldShowSalaryNudge";
import { KeyValueType } from "@/types/common";
import { getSiteProps } from "@/utils";
import { EmploymentApopUI } from "@monsterindia/l1-apop";
import { useImperativeHandle } from "react";

import { useUpdateWorkExp } from "@/components/profile/workExperience/query";

import { WorkFieldProps } from "../../types";

const WorkFields = ({
  prefilledData = null,
  handleLoading,
  closeEdit,
  formRef,
  delRef,
}: WorkFieldProps) => {
  const {
    translation: { errorMessages },
    showToast,
    handleEventTracking,
    hostname,
  } = useProfile();
  const { mutate } = useUpdateWorkExp();
  const isShowHideSalaryToggle = false;

  const siteConfig = getSiteProps(hostname);
  let urlParams = new URLSearchParams(window.location.search);
  const isApp = urlParams.get("app") === "true";
  const onSubmit = async (
    data: any,
    salaryMode: any,
    isCurrentlyWorking: boolean,
  ) => {
    const isRexMonster = siteConfig?.site_map === "rexmonster";

    handleLoading({ isSaveLoading: true });
    const updatedData: KeyValueType = {
      employments: [
        {
          startDate: `${data.startYear.text}-${getMonthNumber(data.startMonth.text)}-01`,
          salary: {
            currency: data.currency.text,
            absoluteValue: data.salary,
            ...(isShowHideSalaryToggle
              ? { confidential: data.hideSalary }
              : {}),
            salaryMode: {
              id: salaryMode.uuid,
            },
          },
          ...(data.title ? { title: data.title } : {}),
          ...(data.employmentType
            ? { employmentType: { text: data.employmentType.text } }
            : {}),
          ...(data.industries
            ? {
                industries: extractValues(data.industries, ["id", "text"]),
              }
            : {}),
        },
      ],
    };

    if (isCurrentlyWorking && data.notice) {
      if (data?.notice?.text === "Serving Notice Period") {
        updatedData["employments"][0]["noticePeriod"] = {
          days: calculateDaysUntil(data.date),
          serving: true,
          lastWorkingDay: data.date,
        };
      } else {
        updatedData["employments"][0]["noticePeriod"] = {
          days: getNoticePeriodToDays(data?.notice?.text),
          serving: false,
          lastWorkingDay: null,
        };
      }
    }

    if (!isCurrentlyWorking) {
      updatedData["employments"][0]["endDate"] =
        `${data.endYear.text}-${getMonthNumber(data.endMonth.text)}-${getLastDateOfMonth(Number(getMonthNumber(data.endMonth.text)), Number(data.endYear.text))}`;
    }

    if (data?.jobTitle?.text === "Others") {
      updatedData["employments"][0]["designation"] = {
        text: data.manualCurrentJobTitle,
      };
    } else {
      updatedData["employments"][0]["designation"] = {
        id: data.jobTitle.id,
      };
    }

    if (data?.company?.text === "Other") {
      updatedData["employments"][0]["company"] = {
        text: data.manualCompany,
      };
    } else {
      updatedData["employments"][0]["company"] = {
        id: data.company.id,
      };
    }

    if (
      data?.notice?.text === "Serving Notice Period" &&
      data.offeredJobTitle
    ) {
      if (data?.offeredJobTitle?.text === "Others") {
        updatedData["employments"][0]["offeredDesignation"] = {
          text: data.manualOfferedJobTitle,
        };
      } else {
        updatedData["employments"][0]["offeredDesignation"] = {
          id: data.offeredJobTitle.id,
        };
      }

      if (data?.newCompany?.text) {
        if (data.newCompany.text === "Other") {
          updatedData["employments"][0]["newCompany"] = {
            text: data.manualNewCompany,
          };
        } else {
          updatedData["employments"][0]["newCompany"] = {
            id: data.newCompany.id,
          };
        }
      }

      if (data?.newOfferedCurrency) {
        updatedData["employments"][0]["offeredSalary"] = {
          currency: data.newOfferedCurrency.text,
          absoluteValue: data.newSalary,
          ...(isShowHideSalaryToggle
            ? { confidential: data.hidePotentialSalary }
            : {}),
          salaryMode: {
            id: salaryMode.uuid,
          },
        };
      }
    }

    if (
      prefilledData &&
      prefilledData?.employmentPrefill &&
      (prefilledData?.employmentPrefill as any)?.id
    ) {
      updatedData.employments[0].id = (
        prefilledData?.employmentPrefill as any
      ).id;
    }

    const action =
      updatedData.employments[0].id &&
      !prefilledData?.employmentPrefill?.isAutoUpdated
        ? "edit"
        : "add";

    mutate(
      { data: updatedData.employments, action },
      {
        onSuccess: () => {
          handleLoading({ isSaveLoading: false });
          showToast({
            show: true,
            message: `Work experience ${action === "edit" ? "updated" : "added"} successfully`,
            type: "success",
          });
          let urlParams = new URLSearchParams(window.location.search);
          const isApp = urlParams.get("app") === "true";
          handleEventTracking({
            eventType: "eventPush",
            eventName: "ProfilePage_Section_Saved",
            eventProps: {
              Work_Exp_Saved: JSON.stringify(updatedData.employments),
              Source: isApp ? "app" : "web",
            },
          });

          if (
            shouldShowSalaryNudge(
              updatedData.employments[0].salary.absoluteValue,
              isRexMonster,
            )
          ) {
            closeEdit("SALARY_NUDGE", {
              actionType: "SALARY",
              salaryPrefill: {
                ...(prefilledData &&
                prefilledData?.employmentPrefill &&
                (prefilledData?.employmentPrefill as any)?.id
                  ? {
                      employmentId: (prefilledData?.employmentPrefill as any)
                        ?.id,
                    }
                  : {}),
                salary: {
                  currency: updatedData.employments[0].salary.currency,
                  absoluteValue:
                    updatedData.employments[0].salary.absoluteValue,
                  salaryMode: {
                    uuid: salaryMode.uuid,
                    text: salaryMode.name,
                  },
                },
              },
            });
          } else {
            closeEdit("save");
          }
        },
        onError: (err) => {
          handleLoading({ isSaveLoading: false });
          showToast({
            show: true,
            message:
              err instanceof Error
                ? err.message
                : errorMessages.somethingWentWrong,
            type: "error",
          });
        },
      },
    );
  };

  const handleDelete = () => {
    handleLoading({ isDeleteLoading: true });
    if (
      prefilledData &&
      prefilledData?.employmentPrefill &&
      (prefilledData?.employmentPrefill as any)?.id
    ) {
      mutate(
        {
          data: { id: (prefilledData?.employmentPrefill as any).id },
          action: "delete",
        },
        {
          onSuccess: () => {
            handleLoading({ isDeleteLoading: false });
            showToast({
              show: true,
              message: "Work experience deleted successfully",
              type: "success",
            });
            handleEventTracking({
              eventType: "eventPush",
              eventName: "ProfilePage_Section_Deleted",
              eventProps: {
                data: JSON.stringify(prefilledData?.employmentPrefill || {}),
                Source: isApp ? "app" : "web",
              },
            });
            closeEdit("delete");
          },
          onError: (err) => {
            handleLoading({ isDeleteLoading: false });
            showToast({
              show: true,
              message:
                err instanceof Error
                  ? err.message
                  : errorMessages.somethingWentWrong,
              type: "error",
            });
          },
        },
      );
    }
  };

  useImperativeHandle(delRef, () => ({
    handleDelete: () => {
      handleDelete();
    },
  }));

  return (
    <EmploymentApopUI
      formRef={formRef}
      prefilledData={prefilledData}
      closeApop={closeEdit} // todo: remove
      flowType="PROFILE_PAGE"
      onSubmit={onSubmit}
      isSubmitAPILoading={false}
      backBtnClickHandler={handleDelete} // todo: remove
    />
  );
};

export default WorkFields;
